import { useEffect, useState } from "react";
import { api } from "../lib/api";
import { BarChart3, Eye, Clock, RefreshCw } from "lucide-react";

function formatSeconds(s) {
    const total = Math.round(s || 0);
    if (total < 60) return `${total}s`;
    const m = Math.floor(total / 60);
    const sec = total % 60;
    if (m < 60) return `${m}m ${sec}s`;
    const h = Math.floor(m / 60);
    return `${h}h ${m % 60}m`;
}

export default function AnalyticsView() {
    const [rows, setRows] = useState([]);
    const [loading, setLoading] = useState(true);

    const load = async () => {
        setLoading(true);
        try {
            const r = await api.get("/admin/analytics/episodes");
            setRows(r.data);
        } finally { setLoading(false); }
    };
    useEffect(() => { load(); }, []);

    const totals = rows.reduce((acc, r) => ({
        sessions: acc.sessions + r.unique_sessions,
        seconds: acc.seconds + r.total_seconds,
    }), { sessions: 0, seconds: 0 });

    return (
        <div data-testid="analytics-view" className="space-y-6">
            <div className="flex items-center justify-between">
                <h2 className="font-display font-bold text-xl inline-flex items-center gap-2">
                    <BarChart3 className="w-5 h-5 text-[#FFD400]" /> Episode Watch Analytics
                </h2>
                <button onClick={load} data-testid="analytics-refresh"
                    className="inline-flex items-center gap-2 text-sm bg-white/5 border border-white/10 rounded-full px-4 py-2 hover:bg-white/10 transition-colors">
                    <RefreshCw className={`w-4 h-4 ${loading ? "animate-spin" : ""}`} /> Refresh
                </button>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                <Stat icon={<Eye className="w-4 h-4" />} label="Total watch sessions" value={totals.sessions} testId="stat-sessions" />
                <Stat icon={<Clock className="w-4 h-4" />} label="Total watch time" value={formatSeconds(totals.seconds)} testId="stat-time" />
                <Stat icon={<BarChart3 className="w-4 h-4" />} label="Tracked episodes" value={rows.length} testId="stat-episodes" />
            </div>

            <div className="rounded-[18px] border border-white/10 bg-white/[0.02] overflow-hidden">
                <div className="overflow-x-auto">
                    <table className="w-full text-sm">
                        <thead className="bg-white/[0.03] text-white/60 text-[11px] uppercase tracking-widest">
                            <tr>
                                <th className="text-left px-4 py-3">Series</th>
                                <th className="text-left px-4 py-3">Episode</th>
                                <th className="text-right px-4 py-3">Sessions</th>
                                <th className="text-right px-4 py-3">Watch time</th>
                                <th className="text-right px-4 py-3">Avg %</th>
                                <th className="text-right px-4 py-3">Max %</th>
                            </tr>
                        </thead>
                        <tbody className="divide-y divide-white/5">
                            {rows.map((r) => (
                                <tr key={r.episode_id} data-testid={`analytics-row-${r.episode_id}`} className="hover:bg-white/[0.02] transition-colors">
                                    <td className="px-4 py-3 font-semibold">{r.series_title}</td>
                                    <td className="px-4 py-3 text-white/80">Part {r.part_number} — {r.episode_title}</td>
                                    <td className="px-4 py-3 text-right tabular-nums">{r.unique_sessions}</td>
                                    <td className="px-4 py-3 text-right tabular-nums">{formatSeconds(r.total_seconds)}</td>
                                    <td className="px-4 py-3 text-right tabular-nums text-white/70">{r.avg_percent}%</td>
                                    <td className="px-4 py-3 text-right tabular-nums">
                                        <span className="inline-block min-w-[3rem] text-right">
                                            <span className={r.max_percent >= 90 ? "text-emerald-400" : r.max_percent >= 50 ? "text-[#FFD400]" : "text-white/70"}>
                                                {r.max_percent}%
                                            </span>
                                        </span>
                                    </td>
                                </tr>
                            ))}
                            {rows.length === 0 && !loading && (
                                <tr>
                                    <td colSpan={6} className="px-4 py-10 text-center text-white/50">
                                        No watch data yet. Data appears as viewers watch episodes.
                                    </td>
                                </tr>
                            )}
                        </tbody>
                    </table>
                </div>
            </div>
        </div>
    );
}

function Stat({ icon, label, value, testId }) {
    return (
        <div data-testid={testId} className="rounded-[18px] border border-white/10 bg-white/[0.02] p-5">
            <div className="text-[10px] uppercase tracking-widest text-white/50 inline-flex items-center gap-1.5">
                {icon} {label}
            </div>
            <div className="font-display font-black text-3xl text-[#FFD400] mt-2 tabular-nums">{value}</div>
        </div>
    );
}
